import { useEffect } from "react";

const SITE_NAME = "Prime Tenancy Cleaning";
const SITE_URL = "https://primetenancycleaning.com";

const setMeta = (name, content, isProperty = false) => {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  if (!href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const SCHEMA_ATTR = "data-seo-schema";

const injectSchemas = (schemas) => {
  // Clear previous page-level schemas
  document.head
    .querySelectorAll(`script[${SCHEMA_ATTR}]`)
    .forEach((n) => n.remove());

  if (!schemas) return;
  const list = Array.isArray(schemas) ? schemas : [schemas];
  list.forEach((schema, idx) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute(SCHEMA_ATTR, `page-${idx}`);
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
  });
};

export const Seo = ({
  title,
  description,
  ogTitle,
  ogDescription,
  path = "/",
  image,
  schema,
}) => {
  const url = `${SITE_URL}${path}`;
  const ogImg =
    image ||
    "https://primetenancycleaning.com/images/kitchen-after.jpg";

  useEffect(() => {
    document.title = `${title} | ${SITE_NAME}`;
    setMeta("description", description);
    setMeta("robots", "index,follow");

    // Open Graph
    setMeta("og:site_name", SITE_NAME, true);
    setMeta("og:title", ogTitle || title, true);
    setMeta("og:description", ogDescription || description, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", url, true);
    setMeta("og:image", ogImg, true);
    setMeta("og:locale", "en_GB", true);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", ogTitle || title);
    setMeta("twitter:description", ogDescription || description);
    setMeta("twitter:image", ogImg);

    setCanonical(url);
    injectSchemas(schema);

    return () => injectSchemas(null);
  }, [title, description, ogTitle, ogDescription, url, ogImg, schema]);

  return null;
};

// Helpers exported for pages
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
});

export const serviceSchema = ({ name, description, serviceType, areaServed = "London" }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType,
  name,
  description,
  provider: {
    "@type": "LocalBusiness",
    name: SITE_NAME,
    telephone: "+442080363586",
    areaServed,
    address: {
      "@type": "PostalAddress",
      addressLocality: "London",
      addressCountry: "GB",
    },
  },
  areaServed: {
    "@type": "City",
    name: areaServed,
  },
});
